import { useSelector } from "react-redux";
import VerifiedIcon from "../../../../../../../assets/svg/VerifiedIcon";
import { visibleScrollbar } from "../../../../../../../utils/functions/get-visible-scrollbar";
import type { RootState } from "../../../../../../../redux/store";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";
import ChartCard, { ChartEmptyState } from "./ChartCard";

export interface SiteTicketData {
  siteName: string;
  totalTickets: number;
  closedTickets: number;
}

interface ClosedTicketsPerSiteProps {
  data: SiteTicketData[];
}

const ClosedTicketsPerSite = ({ data }: ClosedTicketsPerSiteProps) => {
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region);

  return (
    <ChartCard
      title={helpdeskLabels.closedTicketsPerSite}
      description={helpdeskLabels.closedTicketsPerSiteDescription}
      headerIcon={
        <VerifiedIcon className="w-[18px] h-[18px] text-analytics-green dark:text-analytics-green-dk" />
      }
      className="!gap-4 pb-px h-[286px]"
    >
      {data?.length === 0 ? (
        <ChartEmptyState />
      ) : (
      <div
        className={`flex flex-col flex-1 min-h-0 mb-4 h-[286px] overflow-y-auto overflow-x-hidden rounded-[8px] border border-border-gray dark:border-border-gray-dk ${visibleScrollbar(
          "[&::-webkit-scrollbar]:w-[8px]"
        )}`}
      >
        <table className="w-full border-collapse min-w-0">
          <thead className="sticky top-0 z-10">
            <tr className="bg-table-header dark:bg-table-header-dk">
              <th className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk capitalize h-[34px] px-2 border-b border-border-gray dark:border-border-gray-dk">
                Site Name
              </th>
              <th className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk capitalize h-[34px] px-2 w-[140px] border-b border-border-gray dark:border-border-gray-dk">
                Total {helpdeskLabels.ticketPlural}
              </th>
              <th className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk capitalize h-[34px] px-2 w-[140px] border-b border-border-gray dark:border-border-gray-dk">
                Closed {helpdeskLabels.ticketPlural}
              </th>
            </tr>
          </thead>
          <tbody>
            {data?.map((row) => (
              <tr
                key={row?.siteName}
                className="border-b border-gray-75 dark:border-gray-75-dk"
              >
                <td className="h-[34px] px-2">
                  <span className="text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk truncate block">
                    {row?.siteName}
                  </span>
                </td>
                <td className="h-[34px] px-2 w-[140px]">
                  <span className="text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                    {row?.totalTickets}
                  </span>
                </td>
                <td className="h-[34px] px-2 w-[140px]">
                  <span className="inline-flex items-center gap-0.5 h-[18px] pl-1 pr-1.5 py-0.5 rounded bg-verba-green-success dark:bg-verba-green-success-dk text-sm font-semibold text-fixed-white-light-and-dark-mode dark:text-fixed-white-light-and-dark-mode-dk uppercase leading-none">
                    <VerifiedIcon className="w-3 h-3 text-fixed-white-light-and-dark-mode dark:text-fixed-white-light-and-dark-mode-dk" />
                    {row?.closedTickets}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </ChartCard>
  );
};

export default ClosedTicketsPerSite;
